import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ReactDOM from "react-dom";

function PortfolioModal({ isOpen, onClose, project, projectImages }) {
	useEffect(() => {
		const handleEscape = (event) => {
			if (event.key === "Escape") onClose();
		};
		if (isOpen) {
			document.addEventListener("keydown", handleEscape);
			document.body.style.overflow = "hidden";
		}
		return () => {
			document.removeEventListener("keydown", handleEscape);
			document.body.style.overflow = "unset";
		};
	}, [isOpen, onClose]);

	const handleBackdropClick = (e) => {
		if (e.target === e.currentTarget) onClose();
	};
	if (!project) return null;

	return ReactDOM.createPortal(
		<AnimatePresence>
			{isOpen && (
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					onClick={handleBackdropClick}
					className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
				>
					<motion.div
						initial={{ opacity: 0, scale: 0.9, y: 20 }}
						animate={{ opacity: 1, scale: 1, y: 0 }}
						exit={{ opacity: 0, scale: 0.9, y: 20 }}
						transition={{ type: "spring", duration: 0.5 }}
						className="bg-[#1a1a1a]/95 border border-white/10 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
					>
						{/* Header */}
						<div className="sticky top-0 bg-[#1a1a1a]/95 backdrop-blur-sm border-b border-white/10 p-4 sm:p-6 flex items-center justify-between z-10">
							<h2 className="text-2xl sm:text-3xl font-bold text-white">
								{project.name}
							</h2>
							<button
								onClick={onClose}
								className="p-2 hover:bg-white/10 rounded-lg transition-colors text-gray-400 hover:text-white"
								aria-label="Close modal"
							>
								<svg
									className="w-6 h-6"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M6 18L18 6M6 6l12 12"
									/>
								</svg>
							</button>
						</div>

						{/* Content */}
						<div className="p-4 sm:p-6 space-y-6">
							<div className="w-full aspect-[16/9] rounded-xl overflow-hidden bg-white/5 border border-white/10">
								<img
									src={projectImages[project.image]}
									alt={project.name}
									className="w-full h-full object-cover object-center"
									loading="eager"
								/>
							</div>

							<div className="grid md:grid-cols-2 gap-6">
								{/* Left — Description */}
								<div className="space-y-6">
									<div>
										<h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
											<svg
												className="w-5 h-5 text-gray-400"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
												/>
											</svg>
											Project Overview
										</h3>
										<p className="text-gray-300 leading-relaxed">
											{project.description}
										</p>
									</div>

									{project.features && (
										<div>
											<h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
												<svg
													className="w-5 h-5 text-gray-400"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
													/>
												</svg>
												Key Features
											</h3>
											<ul className="space-y-2">
												{project.features.map((feature, index) => (
													<li
														key={index}
														className="text-gray-300 flex items-start gap-2"
													>
														<span className="text-gray-400 mt-1">•</span>
														<span>{feature}</span>
													</li>
												))}
											</ul>
										</div>
									)}
								</div>

								{/* Right — Tech Stack */}
								<div className="space-y-6">
									{project.tech && project.tech.length > 0 && (
										<div>
											<h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
												<svg
													className="w-5 h-5 text-gray-400"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
													/>
												</svg>
												Technologies Used
											</h3>
											<div className="flex flex-wrap gap-2">
												{project.tech.map((tech) => (
													<span
														key={tech}
														className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm hover:border-white/30 transition-colors"
													>
														{tech}
													</span>
												))}
											</div>
										</div>
									)}

									{project.type && (
										<div className="bg-white/5 border border-white/10 rounded-xl p-4">
											<div className="flex items-center justify-between">
												<span className="text-gray-400 text-sm">
													Project Type
												</span>
												<span className="text-white font-medium">
													{project.type}
												</span>
											</div>
										</div>
									)}
								</div>
							</div>

							{/* Actions */}
							<div className="flex gap-4 pt-4 border-t border-white/10">
								{project.repo && (
									<a
										href={project.repo}
										target="_blank"
										rel="noopener noreferrer"
										className="flex-1 py-3 bg-white rounded-lg text-slate-900 font-semibold text-center hover:bg-gray-100 transition-all flex items-center justify-center gap-2"
									>
										<svg
											className="w-5 h-5"
											fill="currentColor"
											viewBox="0 0 24 24"
										>
											<path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
										</svg>
										View Repository
									</a>
								)}
								{project.demo && (
									<a
										href={project.demo}
										target="_blank"
										rel="noopener noreferrer"
										className="flex-1 py-3 bg-white/5 border border-white/10 rounded-lg text-white font-semibold text-center hover:bg-white/10 hover:border-white/30 transition-colors flex items-center justify-center gap-2"
									>
										<svg
											className="w-5 h-5"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
												d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
											/>
										</svg>
										Live Demo
									</a>
								)}
								<button
									onClick={onClose}
									className="px-6 py-3 bg-white/5 border border-white/10 rounded-lg text-gray-300 font-semibold hover:bg-white/10 hover:border-white/30 transition-colors"
								>
									Close
								</button>
							</div>
						</div>
					</motion.div>
				</motion.div>
			)}
		</AnimatePresence>,
		document.getElementById("modal-root")
	);
}

export default PortfolioModal;
